import { Button, Checkbox, InfoIcon, Input, Modal, Select } from "@bp/ui-components";
import type { SelectOptionType } from "@bp/ui-components/dist/@bp/lib/Select/Select";
import { useState } from "react";
import type { MultiValue, SingleValue } from "react-select";
import { useMutation } from "urql";
import { graphql } from "../../../client/gql";
import type { UserUpdateInput } from "../../../client/gql/graphql";
import { InfoBox } from "../../InfoBox/InfoBox";
import { showSuccessToast, showUserErrorToast } from "../../../utils/toast";
import css from "./EditAccountModal.module.scss";

const EditAccountModal_UpdateUser = graphql(`
  mutation EditAccountModal_UpdateUser($where: UserWhere, $update: UserUpdateInput) {
    updateUsers(where: $where, update: $update) {
      users {
        uuid
      }
    }
  }
`);

// The flag has side effects on the backend (open invitation / reset links are
// revoked), so it is not a plain updateUsers field write.
const EditAccountModal_SetImpersonateOnly = graphql(`
  mutation EditAccountModal_SetImpersonateOnly($userUuid: ID!, $impersonateOnly: Boolean!) {
    setImpersonateOnly(userUuid: $userUuid, impersonateOnly: $impersonateOnly) {
      users {
        uuid
      }
    }
  }
`);

// Matches the app's i18n `supportedLngs`.
const LANGUAGE_OPTIONS: SelectOptionType[] = [
  { value: "de", label: "Deutsch" },
  { value: "en", label: "Englisch" },
  { value: "fr", label: "Französisch" },
];

export type EditableAccount = {
  uuid: string;
  email?: string | null;
  preferredLanguage?: string | null;
  impersonateOnly?: boolean | null;
};

type Props = {
  account: EditableAccount;
  onClose: () => void;
  onSaved: () => void;
};

/**
 * Account-level (User) fields. Profile data — name, birthday, role — belongs to
 * the org profile and is edited in `EditProfileModal`.
 */
export function EditAccountModal({ account, onClose, onSaved }: Props) {
  const [email, setEmail] = useState(account.email ?? "");
  const [language, setLanguage] = useState(account.preferredLanguage ?? "");
  const [impersonateOnly, setImpersonateOnly] = useState(Boolean(account.impersonateOnly));
  const [busy, setBusy] = useState(false);
  const [, updateUser] = useMutation(EditAccountModal_UpdateUser);
  const [, setImpersonateOnlyMutation] = useMutation(EditAccountModal_SetImpersonateOnly);

  const trimmedEmail = email.trim();
  const emailChanged = trimmedEmail !== (account.email ?? "");
  const emailValid = trimmedEmail.length === 0 || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  const impersonateOnlyChanged = impersonateOnly !== Boolean(account.impersonateOnly);
  const dirty =
    emailChanged || language !== (account.preferredLanguage ?? "") || impersonateOnlyChanged;

  const save = async () => {
    const update: UserUpdateInput = {};
    if (emailChanged) {
      update.email = trimmedEmail;
    }
    if (language !== (account.preferredLanguage ?? "")) {
      update.preferredLanguage = language || null;
    }
    setBusy(true);
    if (Object.keys(update).length > 0) {
      const res = await updateUser({ where: { uuid: account.uuid }, update });
      if (res.error) {
        setBusy(false);
        showUserErrorToast({ text: "Speichern fehlgeschlagen", error: res.error });
        return;
      }
    }
    if (impersonateOnlyChanged) {
      const res = await setImpersonateOnlyMutation({ userUuid: account.uuid, impersonateOnly });
      if (res.error) {
        setBusy(false);
        showUserErrorToast({ text: "Speichern fehlgeschlagen", error: res.error });
        return;
      }
    }
    setBusy(false);
    showSuccessToast("Konto aktualisiert");
    onSaved();
  };

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      hideFooter={false}
      width="s"
      title="Konto bearbeiten"
      onRequestClose={onClose}
      footer={
        <div className={css.foot}>
          <span className={css.footHint}>Gilt für alle Profile dieser Person.</span>
          <div className={css.footActions}>
            <Button hierarchy="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              hierarchy="primary"
              disabled={busy || !dirty || !emailValid || trimmedEmail.length === 0}
              onClick={() => void save()}
            >
              Speichern
            </Button>
          </div>
        </div>
      }
    >
      <div className={css.stack}>
        <Input
          name="email"
          type="email"
          label="E-Mail (Login)"
          value={email}
          error={emailValid ? undefined : "Keine gültige E-Mail-Adresse"}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Select
          name="preferredLanguage"
          label="Sprache"
          options={LANGUAGE_OPTIONS}
          value={LANGUAGE_OPTIONS.find((o) => o.value === language) ?? null}
          isSearchable={false}
          onChange={(opt: MultiValue<SelectOptionType> | SingleValue<SelectOptionType>) =>
            setLanguage(String((opt as SelectOptionType).value))
          }
        />
        <Checkbox
          name="impersonateOnly"
          label="Nur über Elternaccount"
          checked={impersonateOnly}
          onChange={() => setImpersonateOnly((v) => !v)}
        />
        {impersonateOnly && impersonateOnlyChanged && (
          <InfoBox tone="warning" icon={<InfoIcon style={{ fontSize: 15 }} />}>
            <span style={{ fontSize: 12 }}>
              Ohne eigenen Login gibt es nichts einzulösen: eine offene Einladung und ein offener
              Passwort-Reset werden dabei widerrufen.
            </span>
          </InfoBox>
        )}
        {emailChanged && (
          <InfoBox tone="warning" icon={<InfoIcon style={{ fontSize: 15 }} />}>
            <span style={{ fontSize: 12 }}>
              Die Login-Adresse wird sofort umgestellt — ohne Bestätigungsmail. Der Bestätigungs-
              Status bleibt unverändert und lässt sich im Aktionsmenü nachziehen.
            </span>
          </InfoBox>
        )}
      </div>
    </Modal>
  );
}
